/**
 * InFlightRequestManager - Utility for managing in-flight async requests with deduplication
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for managing in-flight requests to prevent duplicate
 * operations (e.g., multiple pagination requests for the same range).
 */

/**
 * Check if a request is already in flight and return it if so
 * 
 * @param inFlightMap - Map of in-flight requests (key -> Promise)
 * @param key - Request key to check
 * @returns Existing promise if found, null otherwise
 */
export function getInFlightRequest<T>(
  inFlightMap: Map<string, Promise<T>>,
  key: string
): Promise<T> | null {
  return inFlightMap.get(key) || null;
}

/**
 * Check if a request is already in flight
 * 
 * @param inFlightMap - Map of in-flight requests (key -> Promise)
 * @param key - Request key to check
 * @returns True if request is in flight
 */
export function isRequestInFlight<T>(
  inFlightMap: Map<string, Promise<T>>,
  key: string
): boolean {
  return inFlightMap.has(key);
}

/**
 * Store a new in-flight request
 * 
 * @param inFlightMap - Map of in-flight requests (key -> Promise)
 * @param key - Request key
 * @param promise - Promise to store
 */
export function setInFlightRequest<T>(
  inFlightMap: Map<string, Promise<T>>,
  key: string,
  promise: Promise<T>
): void {
  inFlightMap.set(key, promise);
}

/**
 * Remove an in-flight request (cleanup after completion)
 * 
 * @param inFlightMap - Map of in-flight requests (key -> Promise)
 * @param key - Request key to remove
 */
export function removeInFlightRequest<T>(
  inFlightMap: Map<string, Promise<T>>,
  key: string
): void {
  inFlightMap.delete(key);
}

/**
 * Get or create an in-flight request with automatic cleanup
 * 
 * This is a convenience function that:
 * 1. Checks if a request is already in flight
 * 2. Returns the existing promise if found
 * 3. Otherwise, stores the new promise and sets up cleanup
 * 
 * @param inFlightMap - Map of in-flight requests (key -> Promise)
 * @param key - Request key
 * @param createPromise - Function to create a new promise if not in flight
 * @returns Promise (either existing or newly created)
 */
export function getOrCreateInFlightRequest<T>(
  inFlightMap: Map<string, Promise<T>>,
  key: string,
  createPromise: () => Promise<T>
): Promise<T> {
  const existing = getInFlightRequest(inFlightMap, key);
  if (existing) {
    return existing;
  }
  
  const promise = createPromise();
  setInFlightRequest(inFlightMap, key, promise);
  
  // Set up automatic cleanup when promise completes
  promise.finally(() => {
    removeInFlightRequest(inFlightMap, key);
  });
  
  return promise;
}

